import {testIdProps} from '@github-ui/test-id-props'
import {TriangleDownIcon} from '@primer/octicons-react'
import {
  Button,
  SelectPanel,
  type SelectPanelItemInput as ItemInput,
  type SelectPanelItemProps as ItemProps,
} from '@primer/react'
import {useMemo} from 'react'

import type {ExtendedRepository} from '../../api/common-contracts'
import {RepositoryIcon} from '../../components/fields/repository/repository-icon'
import {DefaultRepositoryPickerResources} from '../../strings'
import styles from './repository-picker.module.css'

type Message = {
  variant: 'empty' | 'error' | 'warning'
  title: string
  body: string
}

const defaultNoReposMessage: Message = {
  variant: 'empty',
  title: DefaultRepositoryPickerResources.noReposAvailable,
  body: DefaultRepositoryPickerResources.noReposAvailableBody,
}

const defaultNoReposMatchThisSearchMessage: Message = {
  variant: 'empty',
  title: DefaultRepositoryPickerResources.noReposMatchThisSearch,
  body: DefaultRepositoryPickerResources.noReposMatchThisSearchBody,
}

export function RepositoryPicker({
  selectedRepository,
  repositories,
  query = '',
  loading = false,
  isOpen = false,
  onOpenChange,
  onSearchChange,
  onSelectedChange,
  testIdPrefix = 'repository-picker',
  noResultsMessage,
  noFilterResultsMessage,
  defaultAnchorText,
}: {
  selectedRepository?: ExtendedRepository | null
  repositories: Array<ExtendedRepository>
  query?: string
  loading?: boolean
  isOpen?: boolean
  onOpenChange: (open: boolean) => void
  onSearchChange: (value: string) => void
  onSelectedChange: (selected?: ItemInput) => void
  testIdPrefix?: string
  /** Custom message to display when no repositories are available (empty initial state) */
  noResultsMessage?: Message
  /** Custom message to display when filtering returns no results */
  noFilterResultsMessage?: Message
  /** Default text for the anchor button when no repository is selected */
  defaultAnchorText?: string
}) {
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const availableRepositories = useMemo(
    () => repositories.filter(repo => !repo.isArchived && repo.hasIssues !== false),
    [repositories],
  )

  const displayedItems = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () =>
      availableRepositories.map<ItemProps>(repoItem => ({
        ...repoItem,
        text: repoItem.nameWithOwner,
        leadingVisual: () => <RepositoryIcon repository={repoItem} />,
      })),
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    [availableRepositories],
  )

  // Create a custom selected item display with the appropriate icon
  const selectedItem = useMemo(() => {
    if (!selectedRepository) return undefined

    return {
      id: selectedRepository.id,
      text: selectedRepository.nameWithOwner,
      leadingVisual: () => <RepositoryIcon repository={selectedRepository} />,
    }
  }, [selectedRepository])

  const message = useMemo(() => {
    if (availableRepositories.length > 0) return

    return query !== ''
      ? noFilterResultsMessage || defaultNoReposMatchThisSearchMessage
      : noResultsMessage || defaultNoReposMessage
  }, [availableRepositories.length, query, noResultsMessage, noFilterResultsMessage])

  return (
    <SelectPanel
      className={styles.selectPanel}
      placeholderText={DefaultRepositoryPickerResources.filterPlaceholder}
      placeholder={DefaultRepositoryPickerResources.anchorButtonText}
      renderAnchor={({children, ...anchorProps}) => (
        <Button
          leadingVisual={selectedItem?.leadingVisual}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
          {...testIdProps('repo-suggestions-button')}
        >
          {children || defaultAnchorText || DefaultRepositoryPickerResources.anchorButtonText}
        </Button>
      )}
      open={isOpen}
      onOpenChange={onOpenChange}
      loading={loading}
      selected={selectedItem}
      filterValue={query}
      items={displayedItems}
      showItemDividers
      onFilterChange={onSearchChange}
      onSelectedChange={onSelectedChange}
      message={message}
      overlayProps={{
        onMouseDown: e => e.stopPropagation(),
        height: 'small',
        ...testIdProps(`${testIdPrefix}-list`),
      }}
    />
  )
}

try{ RepositoryPicker.displayName ||= 'RepositoryPicker' } catch {}